/**
 * Video Sync Manager
 * Handles video playback synchronization between host and participants
 */

const SYNC_THRESHOLD = 2.0; // Sync if drift exceeds 2 seconds
const MINOR_DRIFT_THRESHOLD = 0.5; // Minor drift that can be smoothly corrected

/**
 * Calculate the drift between current video time and target time
 * @param {number} currentTime - Current video playback time
 * @param {number} targetTime - Target time from host
 * @returns {number} Drift in seconds (positive = ahead, negative = behind)
 */
export function calculateDrift(currentTime, targetTime) {
  return Math.abs(currentTime - targetTime);
}

/**
 * Check if video needs to be synced based on drift threshold
 * @param {number} drift - Calculated drift in seconds
 * @returns {boolean} True if sync is needed
 */
export function shouldSync(drift) {
  return drift > SYNC_THRESHOLD;
}

/**
 * Check if drift is minor enough for smooth correction
 * @param {number} drift - Calculated drift in seconds
 * @returns {boolean} True if drift is minor
 */
export function isMinorDrift(drift) {
  return drift > MINOR_DRIFT_THRESHOLD && drift <= SYNC_THRESHOLD;
}

/**
 * Sync video to target state
 * @param {HTMLVideoElement} videoElement - The video DOM element
 * @param {number} targetTime - Target playback time
 * @param {boolean} isPlaying - Whether video should be playing
 * @returns {Promise<void>}
 */
export async function syncVideoState(videoElement, targetTime, isPlaying) {
  if (!videoElement) return;

  try {
    const currentTime = videoElement.currentTime;
    const drift = calculateDrift(currentTime, targetTime);

    // Only seek if drift is significant
    if (shouldSync(drift)) {
      console.log(`Syncing video: drift ${drift.toFixed(2)}s`);
      videoElement.currentTime = targetTime;
    }

    // Sync play/pause state
    if (isPlaying && videoElement.paused) {
      await videoElement.play();
    } else if (!isPlaying && !videoElement.paused) {
      videoElement.pause();
    }
  } catch (error) {
    console.error("Error syncing video state:", error);
  }
}

/**
 * Smoothly adjust video playback rate to correct minor drift
 * @param {HTMLVideoElement} videoElement - The video DOM element
 * @param {number} targetTime - Target playback time
 */
export function smoothCorrectDrift(videoElement, targetTime) {
  if (!videoElement) return;

  const currentTime = videoElement.currentTime;
  const drift = calculateDrift(currentTime, targetTime);

  if (isMinorDrift(drift)) {
    // Speed up or slow down slightly to catch up
    if (currentTime < targetTime) {
      videoElement.playbackRate = 1.1; // Play 10% faster
    } else {
      videoElement.playbackRate = 0.9; // Play 10% slower
    }

    // Reset to normal speed after 2 seconds
    setTimeout(() => {
      if (videoElement) {
        videoElement.playbackRate = 1.0;
      }
    }, 2000);
  }
}

/**
 * Format time in seconds to MM:SS format
 * @param {number} seconds - Time in seconds
 * @returns {string} Formatted time string
 */
export function formatTime(seconds) {
  if (!isFinite(seconds)) return "0:00";

  const minutes = Math.floor(seconds / 60);
  const secs = Math.floor(seconds % 60);
  return `${minutes}:${secs.toString().padStart(2, "0")}`;
}

/**
 * Get readable sync status message
 * @param {number} drift - Drift in seconds
 * @returns {string} Status message
 */
export function getSyncStatus(drift) {
  if (drift < MINOR_DRIFT_THRESHOLD) {
    return "In sync";
  } else if (drift < SYNC_THRESHOLD) {
    return "Adjusting...";
  } else {
    return "Syncing...";
  }
}

export default {
  calculateDrift,
  shouldSync,
  isMinorDrift,
  syncVideoState,
  smoothCorrectDrift,
  formatTime,
  getSyncStatus,
  SYNC_THRESHOLD,
  MINOR_DRIFT_THRESHOLD,
};
